import { useCallback, useMemo } from 'react';

import { type CreditUsageState } from '~/components/CreditUsageMeter/types';
import { useDismissedCreditUsageAlertLocalStorageStore } from '~/hooks/creditUsageAlerts/useDismissedCreditUsageAlertLocalStorageStore';
import { useDismissedCreditUsageAlertSessionStorageStore } from '~/hooks/creditUsageAlerts/useDismissedCreditUsageAlertSessionStorageStore';
import { type CreditUsageAlertType, getDismissedCreditUsageAlertStorageKey } from '~/hooks/creditUsageAlerts/utils';

export const useDismissedCreditUsageAlert = ({
  alertType,
  creditUsageState,
  nextCycleStartAt,
  workspaceId,
  persistenceType,
}: {
  alertType: CreditUsageAlertType;
  creditUsageState?: CreditUsageState;
  nextCycleStartAt?: string;
  workspaceId?: string;
  persistenceType: 'localStorage' | 'sessionStorage';
}) => {
  const dismissedAlertsInLocalStorage = useDismissedCreditUsageAlertLocalStorageStore((s) => s.dismissedAlerts);
  const dismissedAlertsInSessionStorage = useDismissedCreditUsageAlertSessionStorageStore((s) => s.dismissedAlerts);

  const dismissInLocalStorage = useDismissedCreditUsageAlertLocalStorageStore((s) => s.dismiss);
  const dismissInSessionStorage = useDismissedCreditUsageAlertSessionStorageStore((s) => s.dismiss);

  const isDismissed = useMemo(() => {
    if (!nextCycleStartAt || !workspaceId || !creditUsageState) {
      return false;
    }

    const dismissedAlerts =
      persistenceType === 'sessionStorage' ? dismissedAlertsInSessionStorage : dismissedAlertsInLocalStorage;

    return (
      dismissedAlerts[getDismissedCreditUsageAlertStorageKey({ workspaceId, alertType, creditUsageState })]
        ?.cycleEndingAt === nextCycleStartAt
    );
  }, [
    alertType,
    nextCycleStartAt,
    dismissedAlertsInLocalStorage,
    dismissedAlertsInSessionStorage,
    workspaceId,
    creditUsageState,
    persistenceType,
  ]);

  const onDismiss = useCallback(() => {
    if (!nextCycleStartAt || !workspaceId || !creditUsageState) {
      return;
    }

    if (persistenceType === 'sessionStorage') {
      dismissInSessionStorage({ alertType, creditUsageState, nextCycleStartAt, workspaceId });
    } else {
      dismissInLocalStorage({ alertType, creditUsageState, nextCycleStartAt, workspaceId });
    }
  }, [
    alertType,
    dismissInLocalStorage,
    dismissInSessionStorage,
    nextCycleStartAt,
    workspaceId,
    creditUsageState,
    persistenceType,
  ]);

  return { isDismissed, onDismiss };
};
